import { DecimalPipe } from '@angular/common';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { Crop, CropVarietiesByCropId, getCrops, getCropsVarietiesByCropId } from '@next/arable-lib/crop';
import { CropField, getCropFields } from '@next/arable-lib/crop-field';
import { getHoveredFieldId, MapFeaturePopUpConfig, PopUpData } from '@next/arable-lib/geo';
import { LocaleService } from '@next/core-lib/i18n';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BlightService } from 'src/app/blight-v2/services/blight.service';

import { SiteState } from 'src/app/shared/models';

import { selectBlightData, selectCustomerBlightRights } from 'src/app/store/selectors';

import { BlightData, Day, PotatoAgrifirmCropIds } from '../../../../blight-v2/models/models';

import { DateService } from '../../../../shared/services/date.service';
import { Feature } from '../map.models';
import { getFeatures } from '../store/map.selectors';

@Injectable({
  providedIn: 'root',
})
export class MapFeaturePopupProvider implements OnDestroy, MapFeaturePopUpConfig {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly pipe = inject(DecimalPipe);
  private readonly blightService = inject(BlightService);
  private readonly dateService = inject(DateService);

  features!: Feature[];
  popupData: BehaviorSubject<PopUpData> = new BehaviorSubject<PopUpData>({
    isVisible: false,
    text: '',
    title: '',
  });
  cropFields: CropField[] = [];
  crops: Crop[] = [];
  cropVarietiesByCropId!: CropVarietiesByCropId[];
  blightDataForToday: Day | undefined;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor() {
    this.init();
  }

  init(): void {
    this.store.pipe(select(getFeatures)).subscribe((features) => {
      this.features = features;
    });

    this.store.pipe(select(getCropFields), takeUntil(this.destroyed$)).subscribe((cropfields) => {
      this.cropFields = cropfields;
    });

    this.store.pipe(select(getCrops), takeUntil(this.destroyed$)).subscribe((crops) => {
      this.crops = crops;
    });

    this.store
      .pipe(select(getCropsVarietiesByCropId), takeUntil(this.destroyed$))
      .subscribe((cropVarietiesByCropId) => {
        this.cropVarietiesByCropId = cropVarietiesByCropId;
      });

    combineLatest([
      this.store.select(selectCustomerBlightRights),
      this.store.select(getHoveredFieldId),
    ]).pipe(takeUntil(this.destroyed$))
      .subscribe(([customerBlightRights, hoveredCropFieldId]) => {
        if (!hoveredCropFieldId) {
          this.popupData.next({
            isVisible: false,
            text: '',
            title: '',
          });
          return;
        }
        const cropfield = this.cropFields.find((cropField) => cropField.id === hoveredCropFieldId);
        if (cropfield) {
          const hoverText = {
            isVisible: true,
            text: this.getCropFieldText(cropfield),
            title: this.getCropFieldTitle(cropfield),
          };

          if (customerBlightRights.isPremium) {
            const agrifirmCropId = this.crops.find((crop) => crop.id === cropfield.cropId)?.agrifirmCrop?.id;
            if (agrifirmCropId !== undefined && PotatoAgrifirmCropIds.includes(agrifirmCropId)) {
              this.store.select(selectBlightData).pipe(
                takeUntil(this.destroyed$)).subscribe((data: BlightData[]) => {
                  if (data) {
                    const blightDaysResult = data.find((x) => x?.cropFieldId === hoveredCropFieldId)?.blightResult?.days;
                    this.blightDataForToday = blightDaysResult?.find((x) => this.dateService.isDateToday(new Date(x.date)));

                    if (this.blightDataForToday &&
                      this.blightDataForToday.sprayType > 0) {
                      hoverText.text += this.getBlightAdviceText();
                    }
                  }
                });
            }
          }
          this.popupData.next(hoverText);
        }
      },
      );
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private getCropFieldTitle(cropField: CropField) {
    const formattedAreaInHectares = this.pipe.transform(
      cropField?.areaInHectares,
      '1.0-3',
      this.locale.getLocale(),
    );
    return `${cropField?.name} / ${formattedAreaInHectares} ${this.locale.translate(
      'hectare-abbreviated',
    )}`;
  }

  private getCropFieldText(cropField: CropField) {
    const cropName = this.crops.find((crop) => crop.id === cropField.cropId)?.agrifirmCrop?.name;
    const cropVarieties = this.cropVarietiesByCropId.find(
      (x) => x.id === cropField.cropId,
    )?.cropVarieties;
    const cropVarietiesName = cropVarieties?.find(
      (cropVariety) => cropVariety.id === cropField.cropVarietyId,
    )?.name;
    return !cropName ? '' : `${cropName} ${cropVarietiesName ? '(' + cropVarietiesName + ')' : ''}`;
  }

  private getBlightAdviceText() {
    if (!this.blightDataForToday) {
      return;
    }
    const sprayTypeName = this.getBlightSprayTypeName(this.blightDataForToday.sprayType);
    // eslint-disable-next-line max-len
    return `\n \n ${this.locale.translate('blight.phytophthora')} ${this.locale.translate('blight.advices')} - ${sprayTypeName} ${this.locale.translate('blight.spraying')}`;
  }

  private getBlightSprayTypeName = (value: number): string => this.blightService.getSprayTypeTranslationForMapLayer(value);
}
